import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchSiteSettings, resolveAssetPath, type SiteSettings } from "../../api";

/** 默认 Logo（API 未配置时使用） */
const DEFAULT_LOGO = "https://storage.readdy-site.link/project_files/870dd70f-e20b-4d95-a3c1-abdd0663e4f5/40837718-58c9-438e-a598-fe499966453f_logo.png?v=01d6c2cfcf3b187e46eae996fab2dd4f";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState<SiteSettings>({});
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === "/";
  const forceWhite = !isHomePage;

  useEffect(() => {
    fetchSiteSettings()
      .then(setSettings)
      .catch(() => {}); // 静默失败，使用默认值
  }, []);

  const logoSrc = resolveAssetPath(settings.site_logo) || DEFAULT_LOGO;
  const siteName = settings.site_name || "Huatera";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "About", path: "/about" },
    { label: "Products", path: "/products" },
    { label: "Solutions", path: "/solutions" },
    { label: "Downloads", path: "/downloads" },
    { label: "News", path: "/news" },
    { label: "Contact", path: "/contact" },
  ];

  const isLight = scrolled || forceWhite;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isLight
          ? "bg-white/95 backdrop-blur-md border-b border-gray-100"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
            className="flex items-center gap-2"
          >
            <img
              src={logoSrc}
              alt={siteName}
              className="h-14 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.path);
                }}
                className={`text-base font-medium transition-colors hover:text-primary ${
                  isLight ? "text-gray-700" : "text-white/90"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <a
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                navigate("/contact");
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-base font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
            >
              Get a Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i
              className={`ri-menu-line text-xl ${
                isLight ? "text-dark" : "text-white"
              }`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(link.path);
                  setMobileMenuOpen(false);
                }}
                className="block text-base font-medium text-gray-700 hover:text-primary py-2"
              >
                {link.label}
              </a>
            ))}
            <a
              href="/contact"
              onClick={(e) => {
                e.preventDefault();
                navigate("/contact");
                setMobileMenuOpen(false);
              }}
              className="block text-center px-5 py-2.5 bg-primary text-white text-base font-medium rounded-full"
            >
              Get a Quote
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}